import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Check, TrendingUp, Bell, BarChart3, Shield, Loader2, ArrowLeft, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { API, getAuthHeaders } from '../utils/api';

// PayPal SVG Icon
const PayPalIcon = () => (
  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.77.77 0 0 1 .757-.64h6.673c2.206 0 3.97.667 5.246 1.984 1.242 1.28 1.792 2.96 1.636 5.001-.16 2.098-.955 3.799-2.363 5.058-1.364 1.22-3.128 1.84-5.244 1.84H9.07a.77.77 0 0 0-.757.64l-.943 5.736a.641.641 0 0 1-.633.538h-1.66z"/>
  </svg>
);

export default function Pricing() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState(null);
  const [paypalLoading, setPaypalLoading] = useState(null);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/payments/plans`);
      setPlans(response.data.plans);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    }
  };

  const handlePayPalCheckout = async (planId) => {
    setPaypalLoading(planId);
    try {
      const response = await axios.post(
        `${API}/payments/paypal/create`,
        {
          plan_id: planId,
          origin_url: window.location.origin
        },
        { 
          withCredentials: true,
          headers: getAuthHeaders()  // Include token for mobile browsers
        }
      );
      
      if (response.data.approval_url) {
        window.location.replace(response.data.approval_url);
      } else {
        toast.error('Failed to get PayPal checkout URL');
        setPaypalLoading(null);
      }
    } catch (error) {
      console.error('PayPal order creation failed:', error);
      setPaypalLoading(null);
      if (error.response?.status === 401) {
        toast.error('Please login first to subscribe');
        navigate('/login');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to connect to PayPal');
      }
    }
  };

  if (!plans) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
          <p className="mt-4 text-slate-400">Loading plans...</p>
        </div>
      </div>
    );
  }

  const features = [
    { icon: TrendingUp, text: 'Unlimited trading signals' },
    { icon: Bell, text: 'Real-time notifications' },
    { icon: BarChart3, text: 'Advanced analytics' },
    { icon: Shield, text: 'Priority support' },
  ];

  return (
    <div className="min-h-screen bg-[#09090B] py-20 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Back Navigation */}
        <div className="flex items-center gap-4 mb-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="text-slate-400 hover:text-white hover:bg-zinc-800"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')}
            className="text-slate-400 hover:text-white hover:bg-zinc-800"
          >
            <Home className="w-4 h-4 mr-2" />
            Dashboard
          </Button>
        </div>

        {/* Header */}
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-emerald-500/10 text-emerald-400 border-emerald-500/30 px-4 py-1.5">
            Simple Pricing
          </Badge>
          <h1 className="text-5xl font-bold font-['Manrope'] mb-4 bg-gradient-to-r from-white to-slate-400 bg-clip-text text-transparent">
            Choose Your Plan
          </h1>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            Start with a 14-day free trial. Cancel anytime. Pay securely with PayPal.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Pro Monthly */}
          <Card className="bg-gradient-to-b from-white/5 to-transparent border-white/10 p-8 hover:border-emerald-500/50 transition-colors">
            <div className="mb-6">
              <h3 className="text-2xl font-bold font-['Manrope'] mb-2">Pro Monthly</h3>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-bold">${plans.pro_monthly.price}</span>
                <span className="text-slate-400">/month</span>
              </div>
              <p className="text-sm text-slate-500 mt-2">Billed monthly</p>
            </div>

            <ul className="space-y-4 mb-8">
              {plans.pro_monthly.features.map((feature, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-300">{feature}</span>
                </li>
              ))}
            </ul>

            <Button
              className="w-full bg-[#0070ba] hover:bg-[#005ea6] text-white font-semibold py-6 text-lg"
              onClick={() => handlePayPalCheckout('pro_monthly')}
              disabled={paypalLoading === 'pro_monthly'}
              data-testid="pro-monthly-paypal-btn"
            >
              {paypalLoading === 'pro_monthly' ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Connecting to PayPal...
                </>
              ) : (
                <>
                  <PayPalIcon />
                  Pay with PayPal
                </>
              )}
            </Button>
          </Card>

          {/* Pro Yearly */}
          <Card className="bg-gradient-to-b from-emerald-500/10 to-transparent border-emerald-500/30 p-8 relative">
            <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-600 text-white px-4">
              Best Value - Save $60
            </Badge>

            <div className="mb-6">
              <h3 className="text-2xl font-bold font-['Manrope'] mb-2">Pro Yearly</h3>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-bold">${plans.pro_yearly.price}</span>
                <span className="text-slate-400">/year</span>
              </div>
              <p className="text-sm text-emerald-400 mt-2">2 months free!</p>
            </div>

            <ul className="space-y-4 mb-8">
              {plans.pro_yearly.features.map((feature, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-300">{feature}</span>
                </li>
              ))}
            </ul>

            <Button
              className="w-full bg-[#0070ba] hover:bg-[#005ea6] text-white font-semibold py-6 text-lg"
              onClick={() => handlePayPalCheckout('pro_yearly')}
              disabled={paypalLoading === 'pro_yearly'}
              data-testid="pro-yearly-paypal-btn"
            >
              {paypalLoading === 'pro_yearly' ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Connecting to PayPal...
                </>
              ) : (
                <>
                  <PayPalIcon />
                  Pay with PayPal
                </>
              )}
            </Button>
          </Card>
        </div>

        {/* Features Grid */}
        <div className="mt-20">
          <h2 className="text-2xl font-bold text-center mb-10">What's Included</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {features.map((feature, idx) => (
              <div key={idx} className="text-center p-6 bg-white/5 rounded-xl border border-white/10">
                <feature.icon className="w-8 h-8 text-emerald-400 mx-auto mb-3" />
                <p className="text-slate-300">{feature.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Trust Badges */}
        <div className="mt-16 text-center">
          <p className="text-slate-500 text-sm mb-4">Secure payments powered by</p>
          <div className="flex items-center justify-center gap-8">
            <div className="flex items-center gap-2 text-[#0070ba]">
              <PayPalIcon />
              <span className="font-bold text-xl">PayPal</span>
            </div>
          </div>
          <p className="text-slate-600 text-xs mt-6">
            256-bit SSL encryption • Cancel anytime • 30-day refund policy
          </p>
        </div>
      </div>
    </div>
  );
}
